import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
// Logo images
const logoWhite = "/Logos/Sayaara White Logo.png";
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { apiService } from '../../services/api';

interface LoginPageProps {
  onLogin: (dealerId: number, dealerName: string) => void;
  onContactClick: () => void;
}

export function LoginPage({ onLogin, onContactClick }: LoginPageProps) {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Determine if identifier is email or username
      const isEmail = formData.identifier.includes('@');
      const loginData = isEmail 
        ? { email: formData.identifier, password: formData.password }
        : { login_username: formData.identifier, password: formData.password };

      const response = await apiService.dealerLogin(loginData);
      
      if (response.success && response.data) {
        toast.success('Login successful!');
        onLogin(response.data.dealer.id, response.data.dealer.name);
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: 'identifier' | 'password', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f91bdc]/5 via-white to-[#f91bdc]/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={logoWhite} 
            alt="Sayaara" 
            className="h-12 mx-auto mb-4" 
          />
          <h1 className="text-2xl font-bold text-gray-900">Dealer Portal</h1>
          <p className="text-gray-600 mt-2">Sign in to manage your inventory</p>
        </div>

        <Card className="border-[#f91bdc]/20 shadow-xl">
          <CardHeader>
            <CardTitle className="text-center text-gray-900">Welcome Back</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access the dealer dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="identifier">Email or Username</Label>
                <Input
                  id="identifier"
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => handleInputChange('identifier', e.target.value)}
                  placeholder="Enter your email or username"
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">
                  Need help accessing your account?
                </p>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={onContactClick}
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Sayaara. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}